import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    domainEventBus,
    RegistrationCreateRequestedEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { ImageRegistration } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';

import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import useSelectedPatient from '../patient/useSelectedPatient';
import useViewportEnvironment from '../useViewportEnvironment';

export default function useSecondaryImage() {
    const [secondaryImages, setSecondaryImages] = useState<ImageRegistration[]>(
        []
    );

    const patient = useSelectedPatient();
    const patientImageCount = patient ? Array.from(patient.images).length : 0;
    const selectedSecondaryImage = useRecoilValue(secondaryImageState);
    const primaryImage = useRecoilValue(primaryImageState);
    const { setEnvironmentSecondaryImage, removeEnvironmentSecondaryImage } =
        useViewportEnvironment();

    useEffect(() => {
        if (!primaryImage || !patient) {
            setSecondaryImages([]);
            return;
        }

        setSecondaryImages(patient.getBlendableSecondaryImages(primaryImage));
    }, [patient, patientImageCount, primaryImage]);

    const handleChangeSecondaryImage = useCallback(
        (
            image: Image3D | null,
            registration?: RigidRegistration | undefined
        ) => {
            // deselect: clear secondary image and registration
            if (!image) {
                removeEnvironmentSecondaryImage();
                return;
            }

            if (!patient?.images?.includes(image) || !primaryImage) {
                return;
            }

            const sharesFOR =
                primaryImage.frameOfReferenceUid === image.frameOfReferenceUid;

            if (registration) {
                setEnvironmentSecondaryImage(image, registration);
            } else if (sharesFOR) {
                // Same Frame of Reference — already spatially aligned,
                // fuse directly without registration.
                setEnvironmentSecondaryImage(image);
            } else {
                // No existing registration — request creation via
                // domain event. The handler will create + activate.
                domainEventBus.publish(
                    new RegistrationCreateRequestedEvent(
                        primaryImage.seriesUid,
                        image.seriesUid
                    )
                );
            }
        },
        [
            patient,
            primaryImage,
            setEnvironmentSecondaryImage,
            removeEnvironmentSecondaryImage,
        ]
    );

    return {
        selectedSecondaryImage,
        secondaryImages,
        handleChangeSecondaryImage,
    };
}
